'use strict';

/**
 * @ngdoc service
 * @name gfcmsApp.job.service
 * @description
 * # job.service
 * Service in the gfcmsApp.
 */
angular.module('gfcmsApp')
  .service('jobService', jobService);

jobService.$inject = ['Upload', 'BaseService', 'errorService', 'jobFactory'];

/**
 * @typedef {Object} JobService
 * @property { (function(string): Promise.<Job, Error>) } getJob
 * @property { (function(string): string) } getCompleteJobUrl
 * @property { (function(string): Promise.<JobArrival>) } arrivedJob
 * @property { (function(JobCompletion): Promise) } completeJob
 * @property { (function(JobPartCompletion): Promise) } partlyCompleteJob
 * @property { (function(JobInterruption): Promise) } interruptedJob
 * @property { (function(JobCancellation): Promise) } cancelledJob
 * @property { (function(JobRescheduled): Promise) } rescheduledJob
 * @property { (function(string): Promise.<JobUpdateInfo>) } getUpdateInfo
 */

/**
 * @param Upload
 * @param BaseService
 * @param errorService
 * @param { JobFactory } jobFactory
 * @return { JobService }
 */
function jobService(Upload, BaseService, errorService, jobFactory) {

  var service = new BaseService();

  service.getJob = getJob;
  service.arrivedJob = arrivedJob;
  service.completeJob = completeJob;
  service.partlyCompleteJob = partlyCompleteJob;
  service.interruptedJob = interruptedJob;
  service.cancelledJob = cancelledJob;
  service.rescheduledJob = rescheduledJob;
  service.getUpdateInfo = getUpdateInfo;

  return service;

  /**
   * @typedef {Object} JobDto
   * @property {String} address
   * @property {String} arrived
   * @property {Date} completeDate
   * @property {String} completedByUserId
   * @property {String} customerId
   * @property {Number} dayId
   * @property {Number} duration
   * @property {String} end
   * @property {String} endDateTime
   * @property {String} homeId
   * @property {String} id
   * @property {String} jobDate
   * @property {Number} jobStatusId
   * @property {Array} logs
   * @property {String} notes
   * @property {String} orderId
   * @property {String} recipient
   * @property {String} scheduleId
   * @property {String} start
   * @property {String} startDateTime
   * @property {String} status
   * @property {String} title
   * @property {boolean} canAcceptUpdate
   */

  /**
   * @param {string} id
   * @return {Promise<Job, Error>}
   */
  function getJob(id) {
    return service.http.get(service.baseUrl + '/jobs/' + id)
      .then(
        /**
         * @param { { data: JobDto} } response
         */
        function (response) {
        jobFactory.create(response.data);
      })
      .catch(handleError);
  }

  /**
   * @typedef {Object} JobArrival
   * @property {string} arrived
   * @param {string} id
   * @return {Promise.<JobArrival>}
   */
  function arrivedJob(id) {
    return service.http.post(service.baseUrl + '/jobs/' + id + '/arrived')
      .then(handleSuccess)
      .catch(handleError);
  }

  /**
   * @typedef {Object} StatusValueMap
   * @property {string} status
   * @property {number} value
   */

  /**
   * @typedef {Object} CustomerInfo
   * @property {string} customer
   * @property {string} address
   */

  /**
   * @typedef {Object} JobUpdateInfo
   * @property {string} status
   * @property {string} arrived
   * @property {string[]} maintenanceServices
   * @property {string[]} lawnCareServices
   * @property {StatusValueMap[]} statusMap
   * @property {string} scheduleId
   * @property {CustomerInfo} customerInfo
   * @param {string} id
   * @return {Promise.<JobUpdateInfo>}
   */
  function getUpdateInfo(id) {
    return service.http.get(service.baseUrl + '/jobs/update/' + id + '/complete')
      .then(handleSuccess)
      .catch(handleError);
  }

  /**
   * @typedef JobCompletion
   * @property {string} jobId
   * @property {string} customerNotes
   * @property {string[]} workCompleted
   * @property {File[]} pictures
   */

  /**
   * @param {JobCompletion} jobCompletion
   * @return {Promise}
   */
  function completeJob(jobCompletion) {
    var url = getCompleteJobUrl(jobCompletion.jobId);

    return Upload.upload({
      url: url,
      method: 'POST',
      data: jobCompletion,
      arrayKey: ''
    });
  }

  /**
   * @typedef JobPartCompletion
   * @property {string} jobId
   * @property {string} notCompletedReason
   * @property {string[]} workCompleted
   * @property {File[]} pictures
   */
  /**
   * @param {JobPartCompletion} jobPartCompletion
   */
  function partlyCompleteJob(jobPartCompletion) {
    var url = service.baseUrl + '/jobs/update/' + jobPartCompletion.jobId + '/partlycomplete';

    return Upload.upload({
      url: url,
      method: 'POST',
      data: jobPartCompletion,
      arrayKey: ''
    });
  }

  /**
   * @typedef JobInterruption
   * @property {string} jobId
   * @property {string} notCompletedReason
   * @property {string[]} workCompleted
   * @property {File[]} pictures
   */
  /**
   * @param {JobInterruption} jobInterruption
   */
  function interruptedJob(jobInterruption) {
    var url = service.baseUrl + '/jobs/update/' + jobInterruption.jobId + '/interrupted';

    return Upload.upload({
      url: url,
      method: 'POST',
      data: jobInterruption,
      arrayKey: ''
    });
  }

  /**
   * @typedef JobCancellation
   * @property {string} jobId
   * @property {string} notCompletedReason
   * @property {string[]} workCompleted
   */
  /**
   * @param {JobCancellation} jobCancellation
   */
  function cancelledJob(jobCancellation) {
    var url = service.baseUrl + '/jobs/update/' + jobCancellation.jobId + '/cancelled';

    return Upload.upload({
      url: url,
      method: 'POST',
      data: jobCancellation,
      arrayKey: ''
    });
  }

  /**
   * @typedef JobRescheduled
   * @property {string} jobId
   * @property {string} notCompletedReason
   * @property {string[]} workCompleted
   */
  /**
   * @param {JobRescheduled} jobRescheduled
   */
  function rescheduledJob(jobRescheduled) {
    var url = service.baseUrl + '/jobs/update/' + jobRescheduled.jobId + '/rescheduled';

    return service.http.post(url, jobRescheduled)
      .then(handleSuccess)
      .catch(handleError);
  }

  /**
   * @param {string} id
   * @returns {string}
   */
  function getCompleteJobUrl(id) {
    return service.baseUrl + '/jobs/update/' + id + '/complete';
  }

  /**
   *
   * @param {{ data: T}}response
   * @return { T }
   */
  function handleSuccess(response) {
    return response.data;
  }

  function handleError(error, status) {
    return service.q.reject(errorService.handleError(error));
  }
}
